import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { api } from '../../api';
import type { HetznerServerOptions, HetznerServerType } from '../../api';
import { useResource } from '../../hooks';
import { Field, Input, Select, Textarea } from '../primitives';
import { ErrorNotice } from '../ui';
import { QuietEmpty } from '../project/ui';

/** What the person chose; the page sends it when they press Create. */
export interface HetznerServerForm {
  tokenId: string;
  location: string;
  serverType: string;
  sshKey: string;
}

export const emptyHetznerForm: HetznerServerForm = { tokenId: '', location: '', serverType: '', sshKey: '' };

/** "cpx32 · 4 vCPU · 8 GB · €13.19 / mo" */
export function serverTypeLabel(t: HetznerServerType, recommended: boolean): string {
  const price = t.monthly_eur > 0 ? ` · €${t.monthly_eur.toFixed(2)} / mo` : '';
  return `${t.name} · ${t.cores} vCPU · ${t.memory_gb} GB${price}${recommended ? ' · recommended' : ''}`;
}

/** The type the form has selected, to show its price on the button. */
export function selectedServerType(options: HetznerServerOptions | null | undefined, form: HetznerServerForm): HetznerServerType | undefined {
  return options?.server_types.find((t) => t.name === form.serverType);
}

/**
 * The Hetzner half of Add server (CO-200): the organization's own token,
 * then the locations and sizes that token's project offers, with the one
 * Commerce Ops recommends preselected. A token is added here when there is
 * none yet, so the first server needs no detour through Settings.
 */
export function HetznerServerFields({
  isOwner,
  form,
  onChange,
  onOptions,
}: {
  isOwner: boolean;
  form: HetznerServerForm;
  onChange: (next: HetznerServerForm) => void;
  onOptions: (options: HetznerServerOptions | null) => void;
}) {
  const [reload, setReload] = useState(0);
  const tokens = useResource(() => (isOwner ? api.listCloudTokens() : Promise.resolve(null)), [isOwner, reload], undefined, { key: 'cloud-tokens' });
  const list = tokens.data?.tokens ?? [];

  // The first token is chosen by itself; a removed one is dropped.
  useEffect(() => {
    if (!tokens.data) return;
    if (list.length > 0 && !list.some((t) => t.id === form.tokenId)) onChange({ ...form, tokenId: list[0]!.id, location: '', serverType: '' });
    if (list.length === 0 && form.tokenId) onChange({ ...form, tokenId: '', location: '', serverType: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokens.data]);

  const options = useResource(
    () => (form.tokenId ? api.getHetznerOptions(form.tokenId) : Promise.resolve(null)),
    [form.tokenId],
    undefined,
    { key: `hetzner-options:${form.tokenId}` },
  );
  const data = options.data ?? null;
  useEffect(() => onOptions(data), [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // The recommendation is preselected once per token.
  useEffect(() => {
    if (!data || form.serverType) return;
    onChange({ ...form, serverType: data.recommended_server_type || data.server_types[0]?.name || '', location: data.recommended_location || data.locations[0]?.name || '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (!isOwner) return <QuietEmpty>Only an owner can create servers on Hetzner</QuietEmpty>;
  if (tokens.error && !tokens.data) return <ErrorNotice error={tokens.error} />;
  if (!tokens.data) return null;

  if (list.length === 0) return <AddToken onSaved={() => setReload((n) => n + 1)} />;

  const types = (data?.server_types ?? []).filter((t) => !form.location || t.locations.includes(form.location));
  return (
    <>
      {list.length > 1 ? (
        <Field label="Hetzner account">
          <Select value={form.tokenId} onChange={(e) => onChange({ ...form, tokenId: e.target.value, location: '', serverType: '' })} data-testid="hetzner-token">
            {list.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        </Field>
      ) : null}
      {options.error && !data ? <ErrorNotice error={options.error} /> : null}
      {data ? (
        <div className="onboarding-grid">
          <Field label="Location">
            <Select
              value={form.location}
              onChange={(e) => {
                const location = e.target.value;
                const keep = data.server_types.find((t) => t.name === form.serverType)?.locations.includes(location);
                onChange({ ...form, location, serverType: keep ? form.serverType : '' });
              }}
              data-testid="hetzner-location"
            >
              {data.locations.map((l) => (
                <option key={l.name} value={l.name}>
                  {l.city}, {l.country} ({l.name})
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Size">
            <Select value={form.serverType} onChange={(e) => onChange({ ...form, serverType: e.target.value })} data-testid="hetzner-type">
              {types.map((t) => (
                <option key={t.name} value={t.name}>
                  {serverTypeLabel(t, t.name === data.recommended_server_type)}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      ) : null}
      <Field label="SSH public key" hint="Optional, to log in yourself">
        <Textarea
          className="input--mono"
          rows={2}
          value={form.sshKey}
          onChange={(e) => onChange({ ...form, sshKey: e.target.value })}
          placeholder="ssh-ed25519 AAAA…"
          spellCheck={false}
          data-testid="hetzner-ssh-key"
        />
      </Field>
    </>
  );
}

function AddToken({ onSaved }: { onSaved: () => void }) {
  const [name, setName] = useState('');
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  async function save(event: FormEvent) {
    event.preventDefault();
    event.stopPropagation();
    setBusy(true);
    setError(null);
    try {
      await api.addCloudToken({ provider: 'hetzner', name: name.trim(), token: token.trim() });
      onSaved();
    } catch (err) {
      setError(err as Error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack" data-testid="hetzner-add-token">
      <ErrorNotice error={error} />
      <Field label="Token name" hint="Shown when you create a server">
        <Input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Production project" autoComplete="off" maxLength={80} data-testid="cloud-token-name" />
      </Field>
      <Field label="Hetzner API token" hint="Read & Write, from the project's Security page">
        <Input type="password" value={token} onChange={(e) => setToken(e.target.value)} autoComplete="off" spellCheck={false} data-testid="cloud-token-value" />
      </Field>
      <div className="row-actions">
        <button type="button" className="button button--secondary" disabled={busy || name.trim() === '' || token.trim() === ''} onClick={(e) => save(e as unknown as FormEvent)} data-testid="cloud-token-save">
          {busy ? 'Checking' : 'Save token'}
        </button>
      </div>
    </div>
  );
}
